import { motion } from "framer-motion";
import { BookOpen, Archive, FileText, Database, Search, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function Library() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="library-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-emerald-600 rounded-full filter blur-[128px] animate-pulse" />
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-teal-600 rounded-full filter blur-[128px] animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm mb-6">
              <BookOpen className="w-4 h-4" />
              <span>Knowledge Repository</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Zodiac <span className="text-emerald-500">Library</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-8">
              Digital knowledge systems, institutional archives, and advanced workforce 
              intelligence knowledge repositories for researchers and practitioners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Collections */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Archive, title: "Institutional Archives", description: "Historical documents and records" },
              { icon: FileText, title: "Research Papers", description: "Published research and studies" },
              { icon: Database, title: "Data Resources", description: "Datasets and analytics" },
              { icon: Search, title: "Search & Discovery", description: "Advanced search capabilities" }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all"
              >
                <feature.icon className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-white/50 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Access Our Knowledge Base
          </h2>
          <p className="text-white/60 mb-8">
            Explore our comprehensive collection of workforce intelligence resources.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/research">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                Browse Library
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Request Access
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
